import { useState, useRef, useEffect, useCallback } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  createCircularAvatar,
  createImagePreview,
  validateImageFile,
  getImageQualityFeedback,
} from "@/utils/imageUtils";

interface UseAvatarUploadProps {
  user: User;
  avatarPreview: string | null;
  onAvatarChange: (avatarUrl: string) => void;
  onProcessedImageChange?: (imageUrl: string, processedBlob?: Blob) => void;
  isUploading?: boolean;
}

export const useAvatarUpload = ({
  user,
  avatarPreview,
  onAvatarChange,
  onProcessedImageChange,
  isUploading = false,
}: UseAvatarUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [qualityFeedback, setQualityFeedback] = useState<string | null>(null);
  const [cropScale, setCropScale] = useState<number>(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [rawPreview, setRawPreview] = useState<string | null>(null);
  const [compressionStats, setCompressionStats] = useState<string | null>(null);
  const [zoomedPreviewUrl, setZoomedPreviewUrl] = useState<string | null>(null);
  const [pendingZoomedBlob, setPendingZoomedBlob] = useState<Blob | null>(null);
  const { toast } = useToast();

  const uploadToSupabase = useCallback(
    async (file: File | Blob, originalFile?: File): Promise<string> => {
      try {
        // Determine file extension and content type
        let fileExt: string;
        let contentType: string;

        if (file instanceof File) {
          fileExt = file.name.split(".").pop() || "jpg";
          contentType = file.type;
        } else {
          // For processed blobs, check if it's a GIF
          if (originalFile && originalFile.type === "image/gif") {
            fileExt = "gif";
            contentType = "image/gif";
          } else {
            fileExt = "webp";
            contentType = "image/webp";
          }
        }

        // Format: userId/timestamp.extension (to match our storage policy folder structure)
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;

        console.log(
          "Prepared file name for upload:",
          fileName,
          "to bucket: avatars"
        );

        // Upload to the avatars bucket
        const { data, error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(fileName, file, {
            cacheControl: contentType === "image/gif" ? "no-cache" : "3600",
            upsert: true,
            contentType: contentType,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);
          throw new Error(uploadError.message);
        }

        // Get the public URL with cache-busting parameter
        const { data: publicUrlData } = supabase.storage
          .from("avatars")
          .getPublicUrl(fileName);

        // Add cache-busting parameter to ensure browser loads the new image
        const publicUrl = `${publicUrlData.publicUrl}?t=${Date.now()}`;

        console.log(
          "Avatar uploaded successfully, public URL:",
          publicUrl
        );

        return publicUrl;
      } catch (error) {
        console.error("Avatar upload failed:", error);
        throw error;
      }
    },
    [user]
  );

  const triggerFileInput = () => {
    console.log("Triggering file input click");
    if (fileInputRef.current && !isUploading && !isProcessing) {
      fileInputRef.current.click();
    }
  };

  // Method to upload the pending zoomed blob to Supabase
  const uploadPendingAvatar = useCallback(async (): Promise<string | null> => {
    if (!pendingZoomedBlob || !selectedFile) {
      console.log("No pending avatar to upload");
      return null;
    }

    try {
      console.log("Uploading pending avatar to Supabase");
      const avatarUrl = await uploadToSupabase(pendingZoomedBlob, selectedFile);

      console.log("Pending avatar uploaded successfully");

      // Clean up blob URL after successful upload
      if (zoomedPreviewUrl && zoomedPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(zoomedPreviewUrl);
      }

      // Update state with the real URL
      setZoomedPreviewUrl(avatarUrl);
      onAvatarChange(avatarUrl);

      if (onProcessedImageChange) {
        onProcessedImageChange(avatarUrl, pendingZoomedBlob);
      }

      toast({
        title: "Success",
        description: "Profile picture uploaded successfully",
      });

      return avatarUrl;
    } catch (error) {
      console.error("Failed to upload pending avatar:", error);
      toast({
        title: "Upload failed",
        description: "Could not upload image to server",
        variant: "destructive",
      });
      throw error;
    }
  }, [pendingZoomedBlob, selectedFile, uploadToSupabase, zoomedPreviewUrl, onAvatarChange, onProcessedImageChange, toast]);

  // Create live preview when zoom changes (no upload)
  useEffect(() => {
    if (!selectedFile) return;

    // Debounce preview generation to avoid excessive processing
    const debounceTimer = setTimeout(async () => {
      try {
        setIsProcessing(true);
        setProcessingError(null);

        const processedBlob = await createCircularAvatar(
          selectedFile,
          cropScale
        );

        if (processedBlob) {
          // Clean up previous preview URL
          if (zoomedPreviewUrl && zoomedPreviewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(zoomedPreviewUrl);
          }

          // Create local blob URL for instant preview
          const localPreviewUrl = URL.createObjectURL(processedBlob);
          setZoomedPreviewUrl(localPreviewUrl);
          setPendingZoomedBlob(processedBlob);

          // Update the preview without uploading
          onAvatarChange(localPreviewUrl);

          console.log(`Live preview updated with zoom scale ${cropScale}`);
        } else {
          setProcessingError(
            "Failed to process image. Please try another file."
          );
        }
      } catch (error) {
        console.error("Image processing error:", error);
        setProcessingError("An error occurred while processing the image.");
      } finally {
        setIsProcessing(false);
      }
    }, 150); // Faster debounce for smoother preview

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [cropScale, selectedFile]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (zoomedPreviewUrl && zoomedPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(zoomedPreviewUrl);
      }
    };
  }, [zoomedPreviewUrl]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    console.log("File selected:", event.target.files);
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const file = event.target.files[0];
    setSelectedFile(file);
    setCropScale(1);

    try {
      setIsProcessing(true);
      setProcessingError(null);
      setQualityFeedback(null);

      console.log("Creating image preview");
      const preview = await createImagePreview(file);
      setRawPreview(preview);

      console.log("Validating image file");
      const validation = await validateImageFile(file);

      if (!validation.valid) {
        setProcessingError(validation.error);
        setIsProcessing(false);
        return;
      }

      if (validation.image) {
        const feedback = getImageQualityFeedback(validation.image);
        setQualityFeedback(feedback);
      }

      console.log("Creating circular avatar preview");
      const processedBlob = await createCircularAvatar(file);

      if (processedBlob) {
        // Clean up previous preview URL
        if (zoomedPreviewUrl && zoomedPreviewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(zoomedPreviewUrl);
        }

        // Create local blob URL for instant preview (don't upload yet)
        const localPreviewUrl = URL.createObjectURL(processedBlob);
        setZoomedPreviewUrl(localPreviewUrl);
        setPendingZoomedBlob(processedBlob);

        console.log("Avatar preview created, will upload on save");
        onAvatarChange(localPreviewUrl);

        if (onProcessedImageChange) {
          console.log("Calling onProcessedImageChange with blob URL");
          onProcessedImageChange(localPreviewUrl, processedBlob);
        }
      } else {
        setProcessingError("Failed to process image. Please try another file.");
      }
    } catch (error) {
      console.error("Image processing error:", error);
      setProcessingError("An error occurred while processing the image.");
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    fileInputRef,
    processingError,
    isProcessing,
    qualityFeedback,
    cropScale,
    selectedFile,
    rawPreview,
    triggerFileInput,
    setCropScale,
    handleFileChange,
    isUploading,
    compressionStats,
    uploadPendingAvatar,
    hasPendingUpload: !!pendingZoomedBlob,
  };
};
